import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { format, parseISO } from 'date-fns';

const ConflictResolutionDialog = ({ open, onClose, conflictData, onAcceptAdjustment, targetName }) => {
  const [selectedOption, setSelectedOption] = React.useState(0);

  if (!conflictData || !conflictData.error) {
    return null;
  }

  const error = conflictData.error;
  const hasAdjustments = error.has_viable_adjustments && error.adjustment_options?.length > 0;

  // Format time for display (show only time, not date)
  const formatTime = (isoString) => {
    try {
      return format(parseISO(isoString), 'HH:mm');
    } catch (e) {
      return isoString;
    }
  };

  // Format full datetime for display
  const formatDateTime = (isoString) => {
    try {
      return format(parseISO(isoString), 'MMM dd, HH:mm');
    } catch (e) {
      return isoString;
    }
  };

  const handleAccept = () => {
    if (hasAdjustments && error.adjustment_options[selectedOption]) {
      onAcceptAdjustment(error.adjustment_options[selectedOption]);
    }
  };

  const getAdjustmentIcon = (type) => {
    switch (type) {
      case 'late_start':
        return '⏩';
      case 'early_end':
        return '⏪';
      case 'gap_window':
        return '📍';
      default:
        return '•';
    }
  };

  const getImpactColor = (percentLost) => {
    if (percentLost === 0) return 'success.main';
    if (percentLost < 10) return 'warning.main';
    if (percentLost < 30) return 'warning.dark';
    return 'error.main';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" fontSize="large" />
          <Typography variant="h6">
            Schedule Conflict Detected
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Conflict Summary */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Your observation "{targetName}" conflicts with {error.conflicts?.length || 0} existing observation(s)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total overlap: {error.total_overlap_minutes} minute{error.total_overlap_minutes !== 1 ? 's' : ''}
          </Typography>
        </Alert>

        {/* Requested Window */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Your Requested Window
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body1">
              {formatDateTime(error.requested_window.start)} - {formatTime(error.requested_window.end)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({error.requested_window.duration_minutes} minutes)
            </Typography>
          </Box>
        </Paper>

        {/* Conflicting Observations */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Conflicting Observations
          </Typography>
          {error.conflicts?.map((conflict, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'error.50' }}>
              <Typography variant="body2" fontWeight="medium">
                {conflict.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(conflict.window.start)} - {formatTime(conflict.window.end)}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Adjustment Options */}
        {hasAdjustments ? (
          <>
            <Typography variant="h6" gutterBottom>
              Available Adjustments
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select an option to adjust your observation window and avoid the conflict:
            </Typography>

            <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(Number(e.target.value))}
              >
                {error.adjustment_options.map((option, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: 'pointer',
                      border: selectedOption === idx ? 2 : 1,
                      borderColor: selectedOption === idx ? 'primary.main' : 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    onClick={() => setSelectedOption(idx)}
                  >
                    <FormControlLabel
                      value={idx}
                      control={<Radio />}
                      label={
                        <Box width="100%">
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="body1" fontWeight="medium">
                              {getAdjustmentIcon(option.type)} {option.description}
                            </Typography>
                            {idx === 0 && (
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                BEST OPTION
                              </Box>
                            )}
                          </Box>

                          <Box display="flex" gap={3} mb={1}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                New Window
                              </Typography>
                              <Typography variant="body2">
                                {formatDateTime(option.adjusted_window.start)} - {formatTime(option.adjusted_window.end)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Duration
                              </Typography>
                              <Typography variant="body2">
                                {option.adjusted_window.duration_minutes} minutes
                              </Typography>
                            </Box>
                          </Box>

                          <Box display="flex" gap={3}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Impact
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: getImpactColor(option.impact.percent_lost) }}
                                fontWeight="medium"
                              >
                                {option.impact.minutes_lost === 0
                                  ? 'No impact'
                                  : `-${option.impact.minutes_lost} min (-${option.impact.percent_lost}%)`}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Meets Minimum
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: option.impact.still_meets_minimum ? 'success.main' : 'error.main' }}
                                fontWeight="medium"
                              >
                                {option.impact.still_meets_minimum ? '✓ Yes' : '✗ No'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>
          </>
        ) : (
          <Alert severity="error">
            <Typography variant="body1" gutterBottom>
              No viable adjustments available
            </Typography>
            <Typography variant="body2">
              The conflict cannot be resolved while maintaining the minimum observing time
              of {error.minimum_observing_time_minutes} minutes. Please try:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Choosing a different date</li>
              <li>Reducing the minimum observing time requirement</li>
              <li>Canceling one of the conflicting observations</li>
            </ul>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {hasAdjustments && (
          <>
            <Button onClick={onClose} variant="outlined">
              Choose Different Date
            </Button>
            <Button onClick={handleAccept} variant="contained" color="primary">
              Accept Adjustment
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
